import React, {useEffect} from "react";
import {FCWithHTMLAttributes} from "../types/reactTypes";
import {useReducedMotion} from "framer-motion";
import clsMerge from "../util/clsMerge";

interface CarouselProps {
	page?: number | null;
	pageClassName?: string;
	showScrollbar?: boolean;
	allowScroll?: boolean;
}

/**
 * Component which renders a horizontal carousel.
 *
 * `page` should be a number corresponding to the index of the page to scroll to.
 * If it is null or undefined, the carousel will not scroll.
 *
 * `pageClassName` should be a string of class names to apply to each individual page element.
 * This is useful primarily for sizing the pages.
 *
 * The `showScrollbar` prop should be a boolean indicating whether or not to show the scrollbar.
 *
 * The `allowScroll` prop should be a boolean indicating whether or not to allow manual scrolling.
 * Note that navigation buttons will still work even if this prop is false.
 */
const Carousel: FCWithHTMLAttributes<CarouselProps> = ({
	children,
	page,
	className,
	pageClassName,
	showScrollbar = false,
	allowScroll = true,
	...props
}) => {
	const carouselRef = React.useRef<HTMLDivElement>(null);
	const shouldReduceMotion = useReducedMotion();
	useEffect(() => {
		if (carouselRef.current && page !== null && page !== undefined) {
			const child = carouselRef.current.children[page] as HTMLElement;
			if (child) {
				child.scrollIntoView({
					behavior: shouldReduceMotion ? "instant" : "smooth",
					inline: "nearest",
					block: "nearest",
				});
			}
		}
	}, [page, shouldReduceMotion]);
	return (
		<div
			ref={carouselRef}
			className={clsMerge(
				"flex overflow-x-auto",
				!showScrollbar && "no-scrollbar",
				!allowScroll && "pointer-events-none touch-none",
				className
			)}
			{...props}
		>
			{React.Children.map(children, (child) => (
				<div className={pageClassName}>{child}</div>
			))}
		</div>
	);
};

export default Carousel;
